import { DragEvent, Draggable, DragOverEvent } from '@shopify/draggable';
import { ListItem } from 'src/app/components/scrolling-list';

export interface RvDraggableItem {
  [key: string]: any;
}

export interface RvDroppableItem {
  [key: string]: any;
}

export interface RvDraggableConfig<
  T extends RvDraggableItem,
  F extends RvDroppableItem,
> {
  items: (T | F)[];
  getSelectedItems: () => ListItem<T>[];
  onItemDrop: (
    sourceItemIds: string[],
    targetItemId: string,
    idProperty: string,
  ) => void;
  containersSelector: string;
  appendTo?: string;
  draggableIdProperty?: keyof T;
  droppableIdProperty?: keyof F;
  handleSelector?: string;
  handleMediaQuery?: string;
}

export class RvDraggableService<
  T extends RvDraggableItem,
  F extends RvDroppableItem,
> {
  private draggable: Draggable;
  private targetItemId: string = null;
  private draggedItemId: string = null;
  private mediaQueryList: MediaQueryList | null = null;
  private mediaQueryListener: (() => void) | null = null;

  constructor(public config: RvDraggableConfig<T, F>) {
    this.initializeDraggable();
    this.setupMediaQueryListener();
  }

  private shouldUseHandle(): boolean {
    if (!this.config.handleSelector) {
      return false;
    }

    if (!this.config.handleMediaQuery) {
      return true;
    }

    return window.matchMedia(this.config.handleMediaQuery).matches;
  }

  private setupMediaQueryListener(): void {
    if (!this.config.handleSelector || !this.config.handleMediaQuery) {
      return;
    }

    this.mediaQueryList = window.matchMedia(this.config.handleMediaQuery);
    this.mediaQueryListener = () => {
      this.reinitializeDraggable();
    };
    this.mediaQueryList.addEventListener('change', this.mediaQueryListener);
  }

  private reinitializeDraggable(): void {
    if (this.draggable) {
      this.draggable.destroy();
      this.draggable = null;
    }

    this.targetItemId = null;
    this.draggedItemId = null;
    this.initializeDraggable();
  }

  private initializeDraggable(): void {
    if (!this.config.containersSelector) return;

    const containers = document.querySelectorAll(
      this.config.containersSelector,
    );

    const options: ConstructorParameters<typeof Draggable>[1] = {
      draggable: '.rv-draggable-item, .rv-droppable-item',
      mirror: {
        appendTo: this.config.appendTo,
        xAxis: true,
        yAxis: true,
        cursorOffsetX: -5,
        cursorOffsetY: 0,
      },
      distance: 15,
    };

    if (this.shouldUseHandle()) {
      options.handle = this.config.handleSelector;
    }

    this.draggable = new Draggable(containers, options);

    this.draggable.on('drag:start', (event: DragEvent) =>
      this.onDragStart(event),
    );
    this.draggable.on('drag:over', (event: DragOverEvent) =>
      this.onDragOver(event),
    );
    this.draggable.on('drag:out', () => this.onDragOut());
    this.draggable.on('drag:stop', () => this.onDragEnd());
    this.draggable.on('mirror:create', () => this.onMirrorCreate());
  }

  private onMirrorCreate(): void {
    const mirror = document.querySelector('.draggable-mirror') as HTMLElement;
    // delete the mirror if it exists
    mirror?.remove();
  }

  private onDragStart(event: DragEvent): void {
    const draggedItem = event.source as HTMLElement;
    if (!draggedItem.classList.contains('rv-draggable-item')) {
      event.cancel();
      return;
    }

    const selectedItemIds = this.config
      .getSelectedItems?.()
      .map((item) => this.getDraggableItemId(item.item as T));
    if (
      selectedItemIds?.length &&
      !selectedItemIds.includes(draggedItem.getAttribute('data-item-id'))
    ) {
      event.cancel();
      return;
    }

    this.draggedItemId = draggedItem.getAttribute('data-item-id');

    this.updateMirrorContent();
    this.updateSelectedItemsVisualState();
  }

  private onDragOver(event: DragOverEvent): void {
    const dropTarget = event.over;
    if (
      dropTarget &&
      dropTarget.classList.contains('rv-droppable-item') &&
      !dropTarget.classList.contains('droppable-not-allowed')
    ) {
      this.targetItemId = dropTarget.getAttribute('data-item-id');
    }
  }

  private onDragOut(): void {
    this.targetItemId = null;
  }

  private onDragEnd(): void {
    const selectedItemIds = this.config
      .getSelectedItems?.()
      .map((item) => this.getDraggableItemId(item.item as T));

    if (
      this.targetItemId !== null &&
      this.targetItemId !== undefined &&
      (!selectedItemIds?.length || !selectedItemIds.includes(this.targetItemId))
    ) {
      if (selectedItemIds.length) {
        this.config.onItemDrop(
          selectedItemIds,
          this.targetItemId,
          this.config.draggableIdProperty as string,
        );
      } else if (this.targetItemId !== this.draggedItemId) {
        this.config.onItemDrop(
          [this.draggedItemId],
          this.targetItemId,
          this.config.draggableIdProperty as string,
        );
      }
    }

    this.updateSelectedItemsVisualState(false);
    this.targetItemId = null;
    this.draggedItemId = null;
  }

  private updateSelectedItemsVisualState(shouldAdd: boolean = true): void {
    const selectedItems = this.config.getSelectedItems?.();
    selectedItems?.forEach((item) => {
      const itemId = this.getDraggableItemId(item.item as T);
      if (itemId === this.draggedItemId) return;

      const itemElement = document.querySelector(`[data-item-id="${itemId}"]`);
      if (shouldAdd && itemId !== this.draggedItemId) {
        itemElement?.classList.add('draggable-source--is-dragging');
      } else if (!shouldAdd) {
        itemElement?.classList.remove('draggable-source--is-dragging');
      }
    });
  }

  private updateMirrorContent(): void {
    // This will be called to update the drag mirror with selected items count
    setTimeout(() => {
      const mirror = document.querySelector(
        '.draggable-mirror',
      ) as HTMLElement | null;
      if (!mirror) return;

      // remove any previous badge(s) for safety
      mirror.querySelectorAll('.drag-count-badge').forEach((n) => n.remove());
      const selectedItems = this.config.getSelectedItems?.() ?? [];

      if (selectedItems.length > 1) {
        // Add stack effect using multiple box-shadows
        let shadowSize = 2;
        let boxShadow = '';
        for (let i = 0; i < Math.min(selectedItems.length - 1, 3); i++) {
          boxShadow += `white ${shadowSize}px ${shadowSize}px 0px, #D1D5DB ${
            shadowSize + 1
          }px ${shadowSize + 1}px 0px, `;
          shadowSize += 3;
        }
        shadowSize -= 3;
        mirror.style.boxShadow = `${boxShadow} rgba(0, 0, 0, 0.15) ${
          shadowSize + 1
        }px ${shadowSize + 9}px 8px`;

        const countBadge = document.createElement('div');
        countBadge.className = 'drag-count-badge draggable-mirror-content';
        countBadge.textContent = selectedItems.length.toString();
        countBadge.style.position = 'absolute';
        countBadge.style.top = '-10px';
        countBadge.style.right = '-10px';
        mirror.appendChild(countBadge);
      }
    });
  }

  public getDraggableItemId(item: T): string {
    const idProperty = this.config.draggableIdProperty || 'id';
    return String(item[idProperty]);
  }

  public getDroppableItemId(item: F): string {
    const idProperty = this.config.droppableIdProperty || 'id';
    return String(item[idProperty]);
  }

  public destroy(): void {
    if (this.mediaQueryList && this.mediaQueryListener) {
      this.mediaQueryList.removeEventListener(
        'change',
        this.mediaQueryListener,
      );
      this.mediaQueryList = null;
      this.mediaQueryListener = null;
    }

    if (this.draggable) {
      this.draggable.destroy();
      this.draggable = null;
    }
  }

  public reset(): void {
    this.targetItemId = null;
  }
}
